import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Trash2, Play, Check, X, RotateCcw, BookOpen, Layers, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { PageHeader } from "@/components/PageHeader";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const credErr = (e, t) => toast.error(e.response?.status === 402 ? e.response.data?.detail?.message : (e.response?.data?.detail || t("common.error")));

function scrollToResult(ref) {
  window.requestAnimationFrame(() => {
    if (!ref.current) return;
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    ref.current.scrollIntoView({
      behavior: reduceMotion ? "auto" : "smooth",
      block: "start",
    });
  });
}

function QuizPlayer({ quiz, onClose }) {
  const { t } = useI18n();
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);
  const submit = async () => {
    const arr = quiz.questions.map((_, i) => answers[i] ?? -1);
    const { data } = await api.post(`/learn/quizzes/${quiz.id}/attempt`, { answers: arr });
    setResult(data); setSubmitted(true);
  };
  return (
    <div className="rounded-2xl border bg-card p-5 space-y-4" data-testid="quiz-player">
      <div className="flex items-center justify-between">
        <h3 className="font-display font-semibold">{quiz.title}</h3>
        {submitted && <span className="text-sm font-semibold text-primary" data-testid="quiz-score">{t("learn.score")}: {result.score}/{result.total}</span>}
      </div>
      {quiz.questions.map((q, i) => (
        <div key={i} className="space-y-2" data-testid={`quiz-q-${i}`}>
          <p className="font-medium text-sm">{i + 1}. {q.question}</p>
          <div className="space-y-1.5">
            {q.options.map((opt, oi) => {
              const chosen = answers[i] === oi;
              const correct = submitted && oi === q.answer_index;
              const wrong = submitted && chosen && oi !== q.answer_index;
              return (
                <button key={oi} disabled={submitted} onClick={() => setAnswers({ ...answers, [i]: oi })} data-testid={`quiz-q-${i}-opt-${oi}`}
                  className={`w-full text-left text-sm px-3 py-2 rounded-lg border flex items-center justify-between transition-colors ${correct ? "bg-green-500/10 border-green-500" : wrong ? "bg-destructive/10 border-destructive" : chosen ? "border-primary bg-primary/5" : "hover:bg-muted"}`}>
                  {opt}{correct && <Check className="h-4 w-4 text-green-600" />}{wrong && <X className="h-4 w-4 text-destructive" />}
                </button>
              );
            })}
          </div>
          {submitted && q.explanation && <p className="text-xs text-muted-foreground">{q.explanation}</p>}
        </div>
      ))}
      <div className="flex gap-2">
        {!submitted ? <Button onClick={submit} className="rounded-full" data-testid="quiz-submit">{t("learn.finish")}</Button>
          : <Button variant="outline" onClick={() => { setSubmitted(false); setAnswers({}); setResult(null); }} className="rounded-full" data-testid="quiz-retry"><RotateCcw className="h-4 w-4 mr-1" />{t("learn.retry")}</Button>}
        <Button variant="ghost" onClick={onClose} data-testid="quiz-close">{t("common.close")}</Button>
      </div>
    </div>
  );
}

function QuizTab() {
  const { t } = useI18n(); const { refreshUser } = useAuth();
  const [quizzes, setQuizzes] = useState([]);
  const [text, setText] = useState(""); const [num, setNum] = useState(5); const [title, setTitle] = useState("");
  const [loading, setLoading] = useState(false); const [active, setActive] = useState(null);
  const resultRef = useRef(null);

  useEffect(() => {
    if (active) scrollToResult(resultRef);
  }, [active]);
  const load = () => api.get("/learn/quizzes").then(({ data }) => setQuizzes(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const gen = async () => {
    if (!text.trim()) return; setLoading(true);
    try { const { data } = await api.post("/learn/quizzes/generate", { source_text: text, num: Number(num), title }); setText(""); setTitle(""); load(); setActive(data); refreshUser(); }
    catch (e) { credErr(e, t); } finally { setLoading(false); }
  };
  const open = async (id) => { const { data } = await api.get(`/learn/quizzes/${id}`); setActive(data); };
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="space-y-4">
        <div className="rounded-2xl border bg-card p-5 space-y-3">
          <Input placeholder={t("work.cvTitle").replace("CV", "Quiz")} value={title} onChange={(e) => setTitle(e.target.value)} data-testid="quiz-title" />
          <Textarea rows={7} placeholder={t("learn.pasteContent")} value={text} onChange={(e) => setText(e.target.value)} data-testid="quiz-source" />
          <div className="flex items-center gap-3">
            <Label className="text-sm">{t("learn.numQuestions")}</Label>
            <Input type="number" min="3" max="15" value={num} onChange={(e) => setNum(e.target.value)} className="w-20" data-testid="quiz-num" />
          </div>
          <Button onClick={gen} disabled={loading} className="rounded-full w-full" data-testid="generate-quiz-btn"><Sparkles className="h-4 w-4 mr-2" />{loading ? t("common.loading") : t("learn.generateQuiz")}</Button>
        </div>
        {active && (
            <div ref={resultRef} className="scroll-mt-6" data-testid="quiz-result">
              <QuizPlayer quiz={active} onClose={() => setActive(null)} />
            </div>
          )}
      </div>
      <div>
        <h3 className="font-display font-semibold mb-3">{t("learn.quiz")}</h3>
        {quizzes.length === 0 && <p className="text-sm text-muted-foreground" data-testid="no-quizzes">{t("learn.noQuizzes")}</p>}
        <div className="space-y-2">
          {quizzes.map((q) => (
            <div key={q.id} className="rounded-xl border bg-card p-3 flex items-center justify-between" data-testid="quiz-card">
              <div><div className="font-medium text-sm">{q.title}</div><div className="text-xs text-muted-foreground">{q.questions?.length || 0} questions</div></div>
              <div className="flex gap-2">
                <button onClick={() => open(q.id)} data-testid="play-quiz"><Play className="h-4 w-4 text-primary" /></button>
                <button onClick={async () => { await api.delete(`/learn/quizzes/${q.id}`).catch(() => {}); load(); }}><Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" /></button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function FlashcardsTab() {
  const { t } = useI18n(); const { refreshUser } = useAuth();
  const [decks, setDecks] = useState([]); const [text, setText] = useState(""); const [title, setTitle] = useState("");
  const [loading, setLoading] = useState(false); const [flipped, setFlipped] = useState({});
  const [newDeckId, setNewDeckId] = useState(null);
  const resultRef = useRef(null);
  const load = () => api.get("/learn/flashcards").then(({ data }) => setDecks(data));
  useEffect(() => { load().catch(() => {}); }, []);

  useEffect(() => {
    if (newDeckId !== null && decks.some((d) => d.id === newDeckId)) {
      scrollToResult(resultRef);
    }
  }, [decks, newDeckId]);

  const gen = async () => {
    if (!text.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.post("/learn/flashcards/generate", { source_text: text, title });
      setText("");
      setTitle("");
      setNewDeckId(data.id);
      await load();
      refreshUser();
      toast.success("Vos flashcards sont prêtes !");
    } catch (e) {
      credErr(e, t);
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="space-y-6">
      <div className="rounded-2xl border bg-card p-5 space-y-3 max-w-2xl">
        <Input placeholder="Titre" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="fc-title" />
        <Textarea rows={5} placeholder={t("learn.pasteContent")} value={text} onChange={(e) => setText(e.target.value)} data-testid="fc-source" />
        <Button onClick={gen} disabled={loading} className="rounded-full" data-testid="generate-fc-btn"><Sparkles className="h-4 w-4 mr-2" />{loading ? t("common.loading") : t("learn.generateFlashcards")}</Button>
      </div>
      {decks.map((deck) => (
        <div
            key={deck.id}
            ref={deck.id === newDeckId ? resultRef : null}
            className={deck.id === newDeckId ? "scroll-mt-6 rounded-xl ring-2 ring-primary/40 p-3" : ""}
            data-testid="fc-deck"
          >
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-display font-semibold">{deck.title}</h3>
            <button onClick={async () => { await api.delete(`/learn/flashcards/${deck.id}`).catch(() => {}); load(); }}><Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" /></button>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {deck.cards?.map((c, i) => {
              const key = `${deck.id}-${i}`;
              return (
                <button key={i} onClick={() => setFlipped((f) => ({ ...f, [key]: !f[key] }))} data-testid={`fc-card-${i}`}
                  className="rounded-xl border bg-card p-4 min-h-[110px] text-left text-sm hover:border-primary transition-colors">
                  <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">{flipped[key] ? "R" : "Q"}</div>
                  {flipped[key] ? c.back : c.front}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function SummariesTab() {
  const { t } = useI18n(); const { refreshUser } = useAuth();
  const [items, setItems] = useState([]); const [text, setText] = useState(""); const [title, setTitle] = useState(""); const [loading, setLoading] = useState(false);
  const [newSummaryId, setNewSummaryId] = useState(null);
  const resultRef = useRef(null);

  const load = () => api.get("/learn/summaries").then(({ data }) => setItems(data));
  useEffect(() => { load().catch(() => {}); }, []);

  useEffect(() => {
    if (newSummaryId !== null && items.some((s) => s.id === newSummaryId)) {
      scrollToResult(resultRef);
    }
  }, [items, newSummaryId]);

  const gen = async () => {
    if (!text.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.post("/learn/summaries/generate", { source_text: text, title });
      setText("");
      setTitle("");
      setNewSummaryId(data.id);
      await load();
      refreshUser();
      toast.success("Votre résumé est prêt !");
    } catch (e) {
      credErr(e, t);
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="rounded-2xl border bg-card p-5 space-y-3">
        <Input placeholder="Titre" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="sum-title" />
        <Textarea rows={8} placeholder={t("learn.pasteContent")} value={text} onChange={(e) => setText(e.target.value)} data-testid="sum-source" />
        <Button onClick={gen} disabled={loading} className="rounded-full w-full" data-testid="generate-sum-btn"><Sparkles className="h-4 w-4 mr-2" />{loading ? t("common.loading") : t("learn.generateSummary")}</Button>
      </div>
      <div className="space-y-3">
        {items.map((s) => (
          <div
              key={s.id}
              ref={s.id === newSummaryId ? resultRef : null}
              className={`rounded-xl border bg-card p-4 scroll-mt-6 ${s.id === newSummaryId ? "ring-2 ring-primary/40" : ""}`}
              data-testid="summary-card"
            >
            <div className="font-medium mb-1">{s.title}</div>
            <p className="text-sm text-muted-foreground whitespace-pre-wrap line-clamp-6">{s.summary}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Learn() {
  const { t } = useI18n();
  return (
    <div className="max-w-6xl mx-auto">
      <PageHeader title={t("learn.title")} subtitle={t("learn.subtitle")} />
      <Tabs defaultValue="quiz">
        <TabsList className="mb-6">
          <TabsTrigger value="quiz" data-testid="tab-quiz"><BookOpen className="h-4 w-4 mr-1.5" />{t("learn.quiz")}</TabsTrigger>
          <TabsTrigger value="flashcards" data-testid="tab-flashcards"><Layers className="h-4 w-4 mr-1.5" />{t("learn.flashcards")}</TabsTrigger>
          <TabsTrigger value="summaries" data-testid="tab-summaries"><FileText className="h-4 w-4 mr-1.5" />{t("learn.summaries")}</TabsTrigger>
        </TabsList>
        <TabsContent value="quiz"><QuizTab /></TabsContent>
        <TabsContent value="flashcards"><FlashcardsTab /></TabsContent>
        <TabsContent value="summaries"><SummariesTab /></TabsContent>
      </Tabs>
    </div>
  );
}
